/**
 * What the Licenses tab is allowed to say. Pure — no React, no fetching.
 *
 * THE DEFECT THIS EXISTS FOR. The tab rendered:
 *
 *     loading ? spinner : licenses.length === 0 ? "No licenses assigned" : table
 *
 * A FAILED FETCH LEAVES THE ARRAY EMPTY, so a tenant whose licences could not
 * be loaded was reported as a tenant that HAS none. Those are opposite facts
 * about a commercial record, and the wrong one invites an operator to go and
 * add a licence the tenant already owns.
 *
 * The tab also `Promise.all`ed the tenant's licences with the licence
 * CATALOGUE, so a catalogue outage blanked licences that had loaded perfectly
 * well. Two independent reads, one shared failure.
 *
 * React is a projection here, never the commercial authority. Nothing in this
 * file computes seats, prices or entitlements; it decides what is honest to
 * SHOW given what the canonical sources returned.
 */

export const VIEW = {
  LOADING: 'loading',
  /** The tenant genuinely owns no licences. */
  EMPTY: 'empty',
  /** We could not find out. NOT the same as EMPTY. */
  ERROR: 'error',
  READY: 'ready',
};

/**
 * Which of the four states the licence panel is in.
 *
 * `error` outranks emptiness, always. "We could not load this" and "there is
 * nothing here" must never collapse into one screen.
 */
export const resolveView = ({ loading, error, licences }) => {
  if (loading) return VIEW.LOADING;
  if (error) return VIEW.ERROR;
  if (!licences || licences.length === 0) return VIEW.EMPTY;
  return VIEW.READY;
};

/**
 * Seat facts, straight from canonical fields.
 *
 * `seats_available` is READ, not recomputed. The server owns the commercial
 * arithmetic; deriving it here would make React a second authority that could
 * quietly disagree with billing.
 */
/**
 * The "free" half of the usage line, as the server states it (A2 review R1 #3).
 *
 * A pool that predates the seat contract has no trustworthy capacity, so the
 * server sends `seats_available: null` with `capacity_reason:
 * "needs_reconciliation"`. Rendering that as "{null} free" printed " free" on
 * every pre-contract tenant. It now says what is true and what fixes it.
 */
export const availabilityText = (licence) => {
  const available = licence?.seats_available;
  if (typeof available === 'number') return `${available} free`;
  if (licence?.capacity_reason === 'needs_reconciliation') return 'Needs reconciliation';
  return 'Capacity unavailable';
};

export const seats = (licence) => ({
  purchased: licence?.seats_purchased ?? null,
  assigned: licence?.seats_assigned ?? null,
  available: licence?.seats_available ?? null,
});

/**
 * Is this licence held by the WORKSPACE rather than assigned to people?
 *
 * THE DEFECT, live 5 Oct 2026 on tenant c81d4516. AI Calling's licence
 * (AI_CALLING_TENANT) is per tenant, and the tab rendered it as a seat pool:
 * "1 seats · 1 used · 0 free" — telling the operator a second user would find
 * no AI Calling seat, for a product that has no seats.
 *
 * Read from what the SERVER states (`tenant_level`, derived there from the
 * catalogue's `seat_based: False` and the row's `assignment_type`), with
 * `assignment_type: 'per_tenant'` as the fallback for a server that predates
 * the flag. No licence code is named here, so the next tenant-level product
 * renders correctly the day it is catalogued.
 */
export const PER_TENANT = 'per_tenant';
export const TENANT_LEVEL_LABEL = 'Whole workspace';

export const isTenantLevel = (licence) => (
  licence?.tenant_level === true || licence?.assignment_type === PER_TENANT
);

/**
 * The quantity a licence is BILLED for. A tenant-level licence is one per
 * workspace whatever its row says — a flat per-tenant price times a stray
 * seat count is a bill nobody agreed to.
 */
export const billedQuantity = (licence) => (
  isTenantLevel(licence) ? 1 : (licence?.seats_purchased || 0)
);

/**
 * The summary cards' "Total Seats" and "Seats Assigned": PEOPLE only. A
 * tenant-level licence has no seats to total, so it is not counted — it was
 * adding AI Calling's "1 seat" (and the admin's stray assignment) to both.
 */
export const seatTotals = (licences) => {
  const seated = (licences || []).filter((l) => l && !isTenantLevel(l));
  return {
    purchased: seated.reduce((sum, l) => sum + (l.seats_purchased || 0), 0),
    assigned: seated.reduce((sum, l) => sum + (l.seats_assigned || 0), 0),
  };
};

/**
 * Does this licence's own numbers contradict each other?
 *
 * Returns a message, or null. The panel SHOWS this rather than silently
 * repairing it: a row where `assigned` exceeds `purchased` is a commercial
 * fact somebody needs to look at, and React quietly clamping it to look tidy
 * would hide an over-assignment from the only people who can fix it.
 *
 * Seat-uncapped licences (no purchased figure) are not inconsistent — they are
 * a different model, and are left alone. So is a TENANT-LEVEL licence: it has
 * no seat capacity to over-assign.
 */
export const seatInconsistency = (licence) => {
  if (isTenantLevel(licence)) return null;
  const { purchased, assigned, available } = seats(licence);
  if (purchased === null || assigned === null) return null;
  if (assigned > purchased) {
    return `${assigned} seats assigned against a capacity of ${purchased}`;
  }
  if (available !== null && available !== purchased - assigned) {
    return `available reports ${available}, but capacity ${purchased} minus assigned ${assigned} is ${purchased - assigned}`;
  }
  return null;
};

/**
 * The edition label, when the licence actually carries one.
 *
 * Returns null rather than guessing. A missing edition is not "Basic": the
 * internal value is what distinguishes a free Data Manager from a pro one, and
 * inventing a label for its absence is how a tenant comes to be described as
 * something nobody sold it.
 */
/**
 * Customer-facing names for the internal edition codes.
 *
 * `free` and `pro` are what the licence RECORD says and what every API expects
 * back. `Basic` and `Automation` are what the product is called. These are two
 * different vocabularies and this map is the only place they meet — a label
 * sent back to an endpoint that expects `pro` is a silently rejected write.
 *
 * Deliberately NOT "Free Plan": the tenant is not on a plan called Free, it
 * holds a Data Manager licence at the Basic edition.
 */
export const EDITION_LABELS = Object.freeze({ free: 'Basic', pro: 'Automation' });

export const editionLabel = (licence, catalogue) => {
  const raw = licence?.edition;
  if (!raw) return null;
  // The catalogue wins when it names an edition — it is the commercial
  // authority and can rename a product without a frontend release.
  const entry = (catalogue || []).find(
    (c) => (c.code || c.license_code) === (licence.license_code || licence.code),
  );
  const named = entry?.editions?.[raw]?.label || entry?.edition_labels?.[raw];
  // Then the known product names, then the internal value. Falling back to the
  // raw code is deliberate: an operator seeing "pro" learns something true,
  // while an operator seeing a guessed label learns something false.
  return named || EDITION_LABELS[raw] || raw;
};

/**
 * What a price CELL says, including when there is no price.
 *
 * `final_price` is null for every licence whose catalogue entry carries no
 * price — which is both of this estate's fixtures — and the tab called
 * `licence.final_price.toFixed(2)` on it directly. That threw
 * `Cannot read properties of null` inside the row `.map`, which React escalated
 * into a blank white page: the whole Licenses tab, not one cell.
 *
 * A GUARD IN JSX IS NOT TESTABLE without a renderer this project does not have,
 * so the decision lives here where it can be asserted directly — the same
 * reason `editionLabel` and `seats` are functions rather than inline ternaries.
 *
 * "Not priced" rather than "$0.00": a licence with no price in the catalogue
 * costs an unknown amount, and zero is a specific and wrong claim about money.
 */
export const UNPRICED = 'Not priced';

export const priceLabel = (licence) => (
  licence?.final_price == null ? UNPRICED : `$${licence.final_price.toFixed(2)}`
);

/**
 * The Edit dialog's "Default:" figure — the licence's catalogue price when it
 * was added (`default_price_snapshot`).
 *
 * It printed `default_price_snapshot?.toFixed(2) || '0.00'`, so a licence the
 * catalogue never priced read "Default: $0.00" (DEV, ENGAGE_SEAT — closure
 * audit 10-06, M5). A stated zero is still a price and still reads $0.00.
 */
export const defaultPriceLabel = (licence) => (
  licence?.default_price_snapshot == null
    ? UNPRICED
    : `$${Number(licence.default_price_snapshot).toFixed(2)}`
);

/**
 * A CATALOGUE row as the Add dialog offers it: name, then its price per unit
 * or "Not priced". It printed `(${default_price}/seat)`, which for a null
 * price read "Engage Seat ($/seat)" — a price with the number missing.
 */
export const catalogueOptionLabel = (row) => {
  const unit = isTenantLevel(row) ? 'tenant' : 'seat';
  const price = row?.default_price == null
    ? UNPRICED
    : `$${Number(row.default_price).toFixed(2)}/${unit}`;
  return `${row?.license_name} (${price})`;
};

/**
 * The catalogue rows an operator may ADD to this tenant: not already held,
 * and not a seat retired into another product. The server marks the latter
 * `included_with` (Form Builder Seat and Survey Builder Seat come with
 * Engage, which grants them itself — `included_with_by_licence`), so no code
 * is named here.
 */
export const addableCatalogue = (catalogue, held) => (catalogue || []).filter(
  (row) => row && !row.included_with
    && !(held || []).some((l) => l.license_id === row.id),
);

export const lineTotalLabel = (licence) => (
  licence?.final_price == null
    ? UNPRICED
    : `$${(licence.final_price * billedQuantity(licence)).toFixed(2)}`
);

/**
 * The monthly bill, and how much of it is unknown.
 *
 * `null * seats` is 0, so summing unpriced licences silently understated the
 * total — a tenant holding two unpriced licences read "$0.00 Monthly Cost",
 * which is a claim about money nobody made. Priced rows are summed; unpriced
 * ones are counted so the surface can say what it does not know.
 */
export const monthlyCost = (licences) => {
  const rows = licences || [];
  const priced = rows.filter((l) => l && l.final_price != null);
  return {
    total: priced.reduce((sum, l) => sum + l.final_price * billedQuantity(l), 0),
    unpricedCount: rows.length - priced.length,
  };
};

/**
 * The value an API expects. NEVER the label.
 *
 * Kept beside the label map so the asymmetry is visible: labels are for
 * reading, codes are for writing, and nothing should ever round-trip a label.
 */
export const editionCode = (licence) => licence?.edition ?? null;

/**
 * Which read failed decides what the panel may claim.
 *
 * The two sources are NOT equal dependencies:
 *
 *   tenant licences  CONTROLLING. Without it we cannot say what the tenant
 *                    owns, so the panel must say it could not find out.
 *   catalogue        ENRICHMENT. It supplies edition LABELS. Losing it costs a
 *                    prettier word, not the commercial truth, so the table
 *                    stands and editions fall back to their internal codes.
 *
 * Blanking a loaded licence table because a label lookup failed is how a
 * control-plane surface tells an operator a tenant owns nothing.
 */
export const resolveSourceFailure = ({ licencesFailed, catalogueFailed }) => {
  if (licencesFailed) {
    return { fatal: true, message: "Cluvik could not load this tenant's licences." };
  }
  if (catalogueFailed) {
    return {
      fatal: false,
      // SAYS WHAT ACTUALLY HAPPENS. This claimed the table had fallen back to
      // internal codes, while `editionLabel` falls back to EDITION_LABELS and
      // still renders "Basic" and "Automation" — so the banner described a
      // degradation the operator could not see, on a table that was correct.
      // Codes appear only for an edition this build has no name for.
      message: 'The licence catalogue is unavailable; editions show their standard product names.',
    };
  }
  return null;
};

/**
 * The second line under a licence's name: the PRODUCT it is for, by the
 * server's name (`product_label`, from the catalogue's `product_labels()`).
 *
 * It printed `license_code` — CHATBOT_SEAT, LIVE_CHAT_SEAT, CLUVIK_AI_TENANT
 * — which is an identifier, not something an operator reads (audit A, 6 Oct
 * 2026). Nothing when the server named no product, or when the product's name
 * is already the licence's name: a line repeating the one above says nothing.
 * Never the code.
 */
export const licenceProductText = (licence) => {
  const product = licence?.product_label;
  if (!product) return null;
  if (String(licence?.license_name || '').trim().toLowerCase()
      === String(product).trim().toLowerCase()) return null;
  return `Product: ${product}`;
};
